import { createFileRoute, Outlet, redirect, useMatches } from '@tanstack/react-router'
import { resolveAuth } from '@/lib/auth'
import { useAuth } from '@/contexts/AuthContext'
import { SidebarProvider, SidebarTrigger, SidebarInset } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/app-sidebar'
import { Separator } from '@/components/ui/separator'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import NotificationBell from '@/components/layout/NotificationBell'
import NotFound from '@/components/NotFound'

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayout,
  notFoundComponent: NotFound,
  // Behind login, no SEO value, and would need the visitor's session cookie
  // forwarded to render anything real server-side — stay client-only, same
  // as before this app had SSR at all. Cascades to every child route.
  ssr: false,
  beforeLoad: async ({ context: { queryClient }, location }) => {
    const auth = await resolveAuth(queryClient)

    if (!auth.isAuthenticated) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }

    // Feeds context.auth for the role guards in dashboard/_visitor,
    // _admin, _store, add.jsx (`context.auth.hasRole(...)`).
    return { auth }
  },
})

function AuthenticatedLayout() {
  const matches = useMatches()

  const breadcrumbs = matches
    .filter((match) => match.routeId !== '__root__' && match.routeId !== '/_authenticated')
    .filter((match, index, array) => {
      const hasTitle = !!match.staticData?.breadcrumbTitle
      const isLast = index === array.length - 1
      return hasTitle || isLast
    })
    .map((match) => ({
      title: match.staticData?.breadcrumbTitle || match.routeId.split('/').pop() || 'Dashboard',
      path: match.pathname,
    }))

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <div className="flex flex-1 items-center gap-2">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => (
                  <BreadcrumbItem key={crumb.path}>
                    {index < breadcrumbs.length - 1 ? (
                      <>
                        <BreadcrumbLink href={crumb.path}>{crumb.title}</BreadcrumbLink>
                        <BreadcrumbSeparator />
                      </>
                    ) : (
                      <BreadcrumbPage>{crumb.title}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="ml-auto px-3">
            <NotificationBell />
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}